// Download logic uses original fileName for W2 download filename (2026 update)
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, getDocs, addDoc, doc, getDoc } from 'firebase/firestore';
import { FIREBASE_DB } from '../firebase.config';
import { FileText, Eye, Download } from 'lucide-react';
import { getW2Url } from '../utils/getW2Url';

const W2Documents = () => {
  const { currentUser } = useAuth();
  const [w2Documents, setW2Documents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const logW2Access = async (documentId, year, accessType) => {
    try {
      // Get user's name from Firestore
      const userDoc = await getDoc(doc(FIREBASE_DB, 'users', currentUser.uid));
      let employeeName = currentUser.email; // Default to email

      if (userDoc.exists()) {
        const userData = userDoc.data();
        // Try to get name from different possible fields
        employeeName = userData.name || userData.employeeData?.name || currentUser.email;
      }

      // Create the access log with the employee name
      await addDoc(collection(FIREBASE_DB, 'w2AccessLogs'), {
        userId: currentUser.uid,
        email: currentUser.email,
        documentId: documentId,
        year: year,
        accessType: accessType, // 'view' or 'download'
        accessedAt: new Date(),
        userAgent: navigator.userAgent,
        ipAddress: 'client-side', // IP is logged server-side for security
        employeeName: employeeName
      });
    } catch (error) {
      console.error('Error logging W2 access:', error);
      // Don't block access if logging fails
    }
  };

  const getTemporaryUrl = async (doc) => {
    try {
      // Request a fresh URL from your backend
      const response = await fetch('/api/getW2Url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          storagePath: doc.storagePath,
          fileName: doc.fileName
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to get temporary URL');
      }
      
      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error('Error getting temporary URL:', error);
      throw error;
    }
  };

  const handleViewW2 = async (doc) => {
    try {
      // Log the access first
      await logW2Access(doc.id, doc.year, 'view');
      
      // Get custom URL with longer expiration
      const url = await getW2Url(doc.storagePath);
      
      // Open with custom URL
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing W2:', error);
      setError('Error accessing W2 document. Please try again later.');
    }
  };

  const handleDownloadW2 = async (doc) => {
    try {
      // Log the download access
      await logW2Access(doc.id, doc.year, 'download');
      
      // Get custom URL with longer expiration
      const url = await getW2Url(doc.storagePath);
      
      // Use custom URL for download
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      // Use the original fileName if available, otherwise fallback
      const downloadName = doc.fileName ? doc.fileName : `W2_${doc.year}.pdf`;
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Error downloading W2:', error);
      setError('Error downloading W2 document. Please try again later.');
    }
  };

  useEffect(() => {
    const fetchW2Documents = async () => {
      if (!currentUser?.email) {
        setError('Please log in to access your W2.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('Fetching W2s for user:', currentUser.uid);
        
        // Query w2Documents collection for the current user
        const w2Query = query(
          collection(FIREBASE_DB, 'w2Documents'),
          where('employeeId', '==', currentUser.uid)
        );
        
        console.log('Querying w2Documents collection for employeeId:', currentUser.uid);
        const querySnapshot = await getDocs(w2Query);
        console.log('Found documents:', querySnapshot.size);
        
        const documents = querySnapshot.docs.map(doc => {
          console.log('Document data:', doc.data());
          return {
            id: doc.id,
            ...doc.data()
          };
        });

        // Sort by year descending
        documents.sort((a, b) => b.year - a.year);
        
        setW2Documents(documents);
        setError(null);
      } catch (error) {
        console.error('Error fetching W2:', error);
        setError('Error loading W2 documents. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchW2Documents();
  }, [currentUser]);

  if (!currentUser?.emailVerified) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <p className="text-sm text-yellow-700">
            Please verify your email to access W2 documents.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
      <h2 className="text-2xl font-semibold text-green-800 mb-6">W2 Documents</h2>

      {loading ? (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      ) : w2Documents.length > 0 ? (
        <div className="space-y-4">
          {w2Documents.map(doc => (
            <div key={doc.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all duration-300 border-b-4 border-transparent hover:border-orange-500">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="bg-orange-100 p-2 rounded-full mr-3 inline-block mb-4">
                    <FileText className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-medium text-green-800">
                    W2 - Tax Year {doc.year}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Click view to open or download to save your W2
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0 space-x-2">
                  <button
                    onClick={() => handleViewW2(doc)}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-500 hover:bg-orange-600"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </button>
                  <button
                    onClick={() => handleDownloadW2(doc)}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-4 text-gray-500">
          No W2 documents available
        </div>
      )}
    </div>
  );
};

export default W2Documents;
